/**
 * **Profil printer per PERANGKAT** — K6: *"printer = data"*.
 *
 * ### ⛔ Lubang yang ditutupnya (2026-08-23)
 * `device.profile.printer` dibaca `buildPrintJob` sejak K27, dan `master.registry` memang menerima
 * `profile` lewat PATCH. Tapi **tak ada satu pun layar untuk mengisinya** — jadi satu-satunya cara
 * menyetel printer adalah memanggil API dengan tangan atau menulis SQL.
 *
 * Akibatnya terlihat di server live: ketiga perangkat PB-PKB (`KSR_PB-PKB`, `LAPTOP_IMRON`,
 * `TB_ALYSIA`) **tak punya profil printer sama sekali**. Kasir menekan Cetak Tagihan, server
 * mencatatnya (201), dan byte-nya tak pernah dirakit — yang terlihat di layar cuma
 * "tercatat, tapi belum tercetak". Setelan yang tak punya layar sama saja dengan setelan yang
 * tak ada.
 *
 * ### ⭐ Transport menentukan bentuk formulirnya
 * `net` menuntut host+port (⚠️ WAJIB IP statis / reservasi DHCP — batasan fisik K6). `usb` dan
 * `bt` bermakna **relatif perangkat**: ia cuma bisa dicetak dari mesin tempat printernya menempel,
 * dan keduanya menuntut `device-agent` (K4). ⛔ Bluetooth juga menuntut **pairing di tingkat OS** —
 * itu di luar jangkauan aplikasi ini, dan layar mengatakannya alih-alih membiarkannya gagal seperti
 * "printer rusak".
 */
import { Alert, Button, Form, Input, InputNumber, Modal, Select, Space, Switch } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PAPER_WIDTHS, normalizeMac } from '@osbpos/contract';
import { api } from '../api/client';

/**
 * Merapikan MAC **sambil diketik**: huruf besar, titik dua otomatis tiap dua digit, non-heks
 * dibuang, berhenti di 12 digit.
 *
 * ⛔ Lubang yang ditutupnya (2026-08-25, dari Imron): kotaknya kosong tanpa bentuk, jadi tak ada
 * yang tahu apakah titik dua perlu diketik. Yang mengetik `7C6CF0E22445` tersimpan dengan rapi —
 * lalu gagal saat mencetak, dengan pesan yang menyebut PROFIL, bukan huruf yang kurang.
 *
 * ⚠️ Ini kenyamanan, bukan kontrol. Yang MEMAKSA ada di server (`master.registry` → `device`);
 * layar yang merapikan tanpa server yang menolak cuma memindahkan lubangnya ke jalur API.
 */
function macKetik(raw: string): string {
  const hex = raw.replace(/[^0-9a-fA-F]/g, '').toUpperCase().slice(0, 12);
  return (hex.match(/.{1,2}/g) ?? []).join(':');
}

/** Bentuk mentah `device.profile` — apa adanya dari server, tanpa asumsi. */
type Profil = Record<string, unknown>;

export interface PrinterProfileEditorProps {
  readonly deviceId: string;
  readonly deviceCode: string;
  readonly profile: Profil | null;
  readonly disabled?: boolean;
  readonly onSaved: () => void;
}

interface Nilai {
  transport: 'net' | 'usb' | 'bt';
  /* ⚠️ `| undefined` eksplisit, bukan `?`: `exactOptionalPropertyTypes` membedakan "tak ada kunci"
     dari "kunci bernilai undefined", dan formulir Ant Design memberi yang KEDUA. */
  host: string | undefined;
  port: number | undefined;
  vendorId: string | undefined;
  productId: string | undefined;
  mac: string | undefined;
  channel: number | undefined;
  paperWidth: 58 | 80;
  codepage: number;
  hasCutter: boolean;
  hasDrawerKick: boolean;
  model: string | undefined;
}

function bacaAwal(profile: Profil | null): Nilai {
  const p = (profile?.['printer'] ?? {}) as Record<string, unknown>;
  const a = (p['address'] ?? {}) as Record<string, unknown>;
  return {
    transport: (a['transport'] as Nilai['transport'] | undefined) ?? 'bt',
    host: a['host'] as string | undefined,
    port: (a['port'] as number | undefined) ?? 9100,
    vendorId: a['vendorId'] as string | undefined,
    productId: a['productId'] as string | undefined,
    mac: a['mac'] as string | undefined,
    channel: (a['channel'] as number | undefined) ?? 1,
    paperWidth: (p['paperWidth'] as 58 | 80 | undefined) ?? 58,
    /* ⚠️ PC437 aman untuk huruf Indonesia; profil yang salah menghasilkan struk yang terbaca
       benar di layar tapi kacau di kertas — dan itu hanya ketahuan dari perangkat nyata. */
    codepage: (p['codepage'] as number | undefined) ?? 0,
    hasCutter: (p['hasCutter'] as boolean | undefined) ?? false,
    hasDrawerKick: (p['hasDrawerKick'] as boolean | undefined) ?? false,
    model: p['model'] as string | undefined,
  };
}

export function PrinterProfileEditor(p: PrinterProfileEditorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm<Nilai>();
  const [transport, setTransport] = useState<Nilai['transport']>(bacaAwal(p.profile).transport);

  const punya = (p.profile?.['printer'] ?? null) !== null;

  const simpan = async (v: Nilai) => {
    setBusy(true);
    try {
      const address =
        v.transport === 'net'
          ? { transport: 'net', host: v.host ?? '', port: v.port ?? 9100 }
          : v.transport === 'usb'
            ? { transport: 'usb', vendorId: v.vendorId ?? '', productId: v.productId ?? '' }
            : { transport: 'bt', mac: normalizeMac(v.mac) ?? v.mac ?? '', channel: v.channel ?? 1 };
      /*
       * ⛔ Profil DIGABUNG, bukan ditimpa: `device.profile` juga menampung setelan lain (mis. laci
       * kas, kelak yang lain lagi). Menimpanya seluruhnya berarti menghapus setelan yang tak ada
       * hubungannya dengan printer — dan hilangnya tak bergejala sampai ada yang mencarinya.
       */
      await api.updateMaster('devices', p.deviceId, {
        profile: {
          ...(p.profile ?? {}),
          printer: {
            address,
            paperWidth: v.paperWidth,
            codepage: v.codepage,
            hasCutter: v.hasCutter,
            hasDrawerKick: v.hasDrawerKick,
            ...(v.model === undefined || v.model === '' ? {} : { model: v.model }),
          },
        },
      });
      setOpen(false);
      p.onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        size="small"
        data-testid={`alat-printer-${p.deviceCode}`}
        disabled={p.disabled === true}
        onClick={() => {
          const awal = bacaAwal(p.profile);
          setTransport(awal.transport);
          form.setFieldsValue(awal);
          setOpen(true);
        }}
      >
        {punya ? t('floor.printer.edit') : t('floor.printer.set')}
      </Button>

      <Modal
        open={open}
        title={`${t('floor.printer.title')} · ${p.deviceCode}`}
        onCancel={() => { setOpen(false); }}
        okText={t('common.save')}
        confirmLoading={busy}
        onOk={() => { void form.validateFields().then(simpan); }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" data-testid="printer-form">
          <Form.Item name="transport" label={t('floor.printer.transport')}>
            <Select
              data-testid="printer-transport"
              onChange={(v: Nilai['transport']) => { setTransport(v); }}
              options={[
                { value: 'bt', label: 'Bluetooth' },
                { value: 'net', label: t('floor.printer.net') },
                { value: 'usb', label: 'USB' },
              ]}
            />
          </Form.Item>

          {/*
            ⛔⛔ Dikatakan, bukan disembunyikan. Bluetooth menuntut pairing di tingkat OS — di luar
            jangkauan aplikasi ini. Printer BT yang belum dipasangkan gagal dengan cara yang tampak
            seperti "printer rusak", dan yang mencarinya akan membongkar hal yang salah.
          */}
          {transport === 'bt' && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                   title={t('floor.printer.btHint')} />
          )}
          {transport === 'net' && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
                   title={t('floor.printer.netHint')} />
          )}

          {transport === 'net' && (
            <Space align="start" wrap>
              <Form.Item name="host" label={t('floor.printer.host')}
                         rules={[{ required: true }]}>
                <Input data-testid="printer-host" placeholder="192.168.1.50" />
              </Form.Item>
              <Form.Item name="port" label={t('floor.printer.port')}>
                <InputNumber data-testid="printer-port" min={1} max={65535} />
              </Form.Item>
            </Space>
          )}
          {transport === 'usb' && (
            <Space align="start" wrap>
              <Form.Item name="vendorId" label="Vendor ID" rules={[{ required: true }]}>
                <Input data-testid="printer-vendor" placeholder="0x0416" />
              </Form.Item>
              <Form.Item name="productId" label="Product ID" rules={[{ required: true }]}>
                <Input data-testid="printer-product" placeholder="0x5011" />
              </Form.Item>
            </Space>
          )}
          {transport === 'bt' && (
            <Space align="start" wrap>
              <Form.Item
                name="mac"
                label="MAC"
                tooltip={t('floor.printer.macHint')}
                normalize={macKetik}
                rules={[
                  { required: true },
                  {
                    /* ⚠️ Kosong dilewatkan — biar `required` yang bicara, bukan dua pesan sekaligus. */
                    validator: (_r, v: unknown) =>
                      v === undefined || v === '' || normalizeMac(v) !== null
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('floor.printer.macBad'))),
                  },
                ]}
              >
                <Input data-testid="printer-mac" placeholder="7C:6C:F0:E2:24:45" maxLength={17} />
              </Form.Item>
              <Form.Item name="channel" label="RFCOMM">
                <InputNumber data-testid="printer-channel" min={1} max={30} />
              </Form.Item>
            </Space>
          )}

          <Space align="start" wrap>
            <Form.Item name="paperWidth" label={t('floor.printer.paper')}>
              <Select
                data-testid="printer-paper"
                style={{ width: 120 }}
                options={PAPER_WIDTHS.map((w) => ({ value: w, label: `${String(w)} mm` }))}
              />
            </Form.Item>
            <Form.Item name="codepage" label={t('floor.printer.codepage')}
                       tooltip={t('floor.printer.codepageHint')}>
              <InputNumber data-testid="printer-codepage" min={0} max={255} />
            </Form.Item>
            <Form.Item name="model" label={t('floor.printer.model')}>
              <Input data-testid="printer-model" placeholder="EPSON TM-T82" />
            </Form.Item>
          </Space>

          <Space align="start" wrap>
            <Form.Item name="hasCutter" label={t('floor.printer.cutter')} valuePropName="checked">
              <Switch data-testid="printer-cutter" />
            </Form.Item>
            {/* ⚠️ Tendangan laci lahir dari `payment_method.opens_drawer`, TAPI ia cuma sampai ke
                kertas kalau printernya memang punya port RJ11 (K27). */}
            <Form.Item name="hasDrawerKick" label={t('floor.printer.drawer')} valuePropName="checked">
              <Switch data-testid="printer-drawer" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </>
  );
}
